import { TutorialDefinition } from '../TutorialEngine.js';
import { TurnsRenderOptions } from './TurnsTutorialAdapter.js';
import { COLORS, colorWithAlpha } from '../../rendering/Renderer.js';

type TurnsScene = {
    gameState: string;
    renderOptions: TurnsRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Reference grid 6/1-3:1B3,2C1,3E2 — solved by C0cC3cE0c (3 turns, 17 cells driven,
// score 317), clearing 1 → 2 → 3. Label cells are picked from the space each scene's
// drive leaves empty, so the pills don't sit on top of the lanes.

const scenes: TurnsScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'C0cC3cE0c',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Turns, guide a cart to\nclear all digits in order', cell: 'D2', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Where the cart enters, and the two things that bend it
    {
        gameState: 'C0c',
        renderOptions: {},
        annotations: [
            { type: 'highlight', cells: ['A0'], color: colorWithAlpha('teal', 0.2) },
            { type: 'text-label', text: 'The cart\nenters here', cell: 'A0', anchor: 'far-below' },
            { type: 'text-label', text: 'Turns left or\nright at arrows', cell: 'E1', anchor: 'center' },
            { type: 'text-label', text: 'Turns back\nat edges', cell: 'C5', anchor: 'right' },
            { type: 'pulse', cell: 'C0', color: COLORS.teal },
        ],
    },

    // Scene 3: Placing a turn — the cycle none → ↻ → ↺ — alongside the click-a-digit aid,
    // shown live on digit 1 (the highlight follows each frame's redrawn drive).
    {
        gameState: 'C0cC3c',
        renderOptions: { selectDigit: 1 },
        annotations: [
            { type: 'text-label', text: 'Click empty cells to place\nan arrow and adjust direction', cell: 'C1', anchor: 'center' },
            { type: 'text-label', text: 'Click digits\nto see the path', cell: 'D5', anchor: 'center' },
            { type: 'pulse', cell: 'E0', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: ['C0cC3c', 'C0cC3cE0c', 'C0cC3cE0a'],
        },
    },

    // Scene 4: The clearing rule, read off the solved drive's badges.
    {
        gameState: 'C0cC3cE0c',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A digit is cleared when\npassed the second time', cell: 'B5', anchor: 'center' },
            { type: 'text-label', text: 'Digits must be\ncleared in order', cell: 'D1', anchor: 'center' },
        ],
    },

    // Scene 5: Over-crossing. The extra turn at B0 routes the drive through digit 1 four
    // times: it shows a red square instead of badges, while 2 and 3 still badge normally.
    {
        gameState: 'C0cC3cE0cB0c',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'A digit must not\nbe crossed again', cell: 'D4', anchor: 'center', color: COLORS.red },
            { type: 'text-label', text: 'Badges show order\nof passage', cell: 'E0', anchor: 'center' },
            { type: 'pulse', cell: 'B3', color: COLORS.red },
        ],
    },
];

/**
 * Turns tutorial: 5 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/turns.md for detailed scene descriptions.
 */
export const TURNS_TUTORIAL: TutorialDefinition = {
    gameType: 'turns',
    gridCode: '6/1-3:1B3,2C1,3E2',
    scenes,
};
